import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogType?: string;
  structuredData?: object;
}

export default function SEOHead({ 
  title, 
  description, 
  keywords, 
  canonical, 
  ogType = 'website',
  structuredData 
}: SEOHeadProps) {
  useEffect(() => {
    // Update document title
    document.title = title;
    
    // Update meta tags
    const updateMetaTag = (name: string, content: string, property = false) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.content = content;
    };
    
    // Update link tags
    const updateLinkTag = (rel: string, href: string) => {
      let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        document.head.appendChild(link);
      }
      link.href = href;
    };
    
    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('last-modified', new Date().toISOString().split('T')[0]);
    updateMetaTag('robots', 'index, follow');
    updateMetaTag('author', 'Allgo Digital Sdn Bhd');
    
    // Geo tags for local SEO (Kuala Lumpur, Malaysia)
    updateMetaTag('geo.region', 'MY-14');
    updateMetaTag('geo.placename', 'Kuala Lumpur');
    updateMetaTag('geo.position', '3.1390;101.6869');
    updateMetaTag('ICBM', '3.1390, 101.6869');
    
    // Open Graph tags
    const siteUrl = import.meta.env.VITE_SITE_URL || '';
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:url', `${siteUrl}${canonical}`, true);
    updateMetaTag('og:image', `${siteUrl}/og-image.jpg`, true);
    updateMetaTag('og:site_name', 'Allgo Digital - Solution Platform', true);
    updateMetaTag('og:locale', 'en_US', true);
    
    // Twitter tags
    updateMetaTag('twitter:card', 'summary_large_image', true);
    updateMetaTag('twitter:title', title, true);
    updateMetaTag('twitter:description', description, true);
    updateMetaTag('twitter:url', `${siteUrl}${canonical}`, true);
    updateMetaTag('twitter:image', `${siteUrl}/og-image.jpg`, true);
    updateMetaTag('twitter:site', '@AllgoDigital', true);
    updateMetaTag('twitter:creator', '@AllgoDigital', true);
    
    // Additional SEO meta tags
    updateMetaTag('theme-color', '#2563eb');
    updateMetaTag('msapplication-TileColor', '#2563eb');
    updateMetaTag('application-name', 'Allgo Digital Solution Platform');
    
    // Canonical URL
    updateLinkTag('canonical', `${siteUrl}${canonical}`);
    
    // Structured data
    if (structuredData) {
      // Remove existing structured data
      const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
      existingScripts.forEach(script => script.remove());
      
      // Add new structured data with environment variable
      const dataWithUrl = JSON.parse(JSON.stringify(structuredData));
      
      // Replace placeholder URLs with actual environment variable
      const replaceUrls = (obj: any): any => {
        if (typeof obj === 'string' && obj.includes('import.meta.env.VITE_SITE_URL')) {
          return obj.replace(/\$\{import\.meta\.env\.VITE_SITE_URL[^}]*\}/g, siteUrl);
        }
        if (typeof obj === 'object' && obj !== null) {
          for (const key in obj) {
            obj[key] = replaceUrls(obj[key]);
          }
        }
        return obj;
      };
      
      const processedData = replaceUrls(dataWithUrl);
      
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(processedData);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, canonical, ogType, structuredData]);
  
  return null;
}
